import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = [
  "Mini Category (Ages 4–7)",
  "Little Category (Ages 8–10)",
  "Pre-Teen (Ages 11–14)",
  "Teen (Ages 15–18)",
  "Miss Top Universe (Ages 19–27)",
  "Mr Top Universe (Ages 19–27)",
  "Mrs Category (Ages 29–40)",
  "Grooming & Training Academy",
  "Other Enquiry",
];

const inputCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3 text-sm font-light tracking-wide text-white placeholder:text-neutral-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]";
const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.3em] text-neutral-400";

const PhotoSlot = ({ label, hint, value, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${window.location.origin}${data.url}`);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="border border-white/15 bg-black/30 p-5">
      <span className={labelCls}>{label}</span>
      {value ? (
        <img src={value} alt={label} data-testid={`${testid}-preview`} className="mb-3 h-48 w-full bg-black object-contain" />
      ) : (
        <div className="mb-3 flex h-48 items-center justify-center border border-dashed border-white/15 text-neutral-600">
          <Upload className="h-6 w-6" strokeWidth={1} />
        </div>
      )}
      <label
        data-testid={testid}
        className="flex cursor-pointer items-center justify-center gap-2 border border-[#D4AF37]/50 px-4 py-3 text-xs uppercase tracking-[0.25em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-black"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Upload className="h-4 w-4" strokeWidth={1.5} />}
        {value ? "Change Photo" : hint}
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </label>
    </div>
  );
};

const Field = ({ label, name, form, set, type = "text", required, placeholder, textarea, rows = 3, testid }) => (
  <div>
    <label className={labelCls}>
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    {textarea ? (
      <textarea data-testid={testid} rows={rows} value={form[name] || ""} onChange={set(name)} placeholder={placeholder} className={`${inputCls} resize-y`} />
    ) : (
      <input data-testid={testid} type={type} value={form[name] || ""} onChange={set(name)} placeholder={placeholder} className={inputCls} />
    )}
  </div>
);

const Section = ({ num, title, children }) => (
  <section className="border border-white/10 bg-[#0C0C0C] p-6 md:p-10">
    <div className="mb-8 flex items-baseline gap-4">
      <span className="font-display text-4xl font-light text-[#D4AF37]/40">{num}</span>
      <h2 className="font-display text-2xl font-medium text-white md:text-3xl">{title}</h2>
    </div>
    {children}
  </section>
);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Register() {
  const [form, setForm] = useState({ category: CATEGORIES[0], sex: "Female", agreed: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setPhoto = (k) => (url) => setForm((f) => ({ ...f, [k]: url }));

  const startPayment = async (regId, regData) => {
    try {
      const cfg = (await api.get("/payments/config")).data;
      if (!cfg.enabled) {
        setDone({ id: regId, payment: "pending" });
        return;
      }
      const ord = (await api.post("/payments/create-order", { registration_id: regId })).data;
      const loaded = await loadRazorpay();
      if (!loaded) {
        setDone({ id: regId, payment: "pending" });
        return;
      }
      const rzp = new window.Razorpay({
        key: cfg.key_id,
        amount: ord.amount,
        currency: ord.currency,
        order_id: ord.order_id,
        name: "D'La Valentina",
        description: "Participation Fee — Rs 999",
        prefill: {
          name: `${regData.first_name || ""} ${regData.family_name || ""}`.trim(),
          email: regData.email,
          contact: regData.phone,
        },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { registration_id: regId, ...resp });
            setDone({ id: regId, payment: "paid" });
          } catch {
            setDone({ id: regId, payment: "pending" });
          }
          window.scrollTo(0, 0);
        },
        modal: { ondismiss: () => setDone({ id: regId, payment: "pending" }) },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err));
      setDone({ id: regId, payment: "pending" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed) {
      toast.error("Please accept the declaration to submit your application");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", form);
      await startPayment(data.id, form);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="registration-success" className="flex min-h-screen flex-col items-center justify-center bg-[#050505] px-6 text-center text-white">
        <Crown className="animate-crown-pulse h-14 w-14 text-[#D4AF37]" strokeWidth={1} />
        <h1 className="font-display mt-8 text-4xl font-medium md:text-5xl">
          {done.payment === "paid" ? "Payment Received — You're Registered" : "Your Application Has Been Received"}
        </h1>
        <p className="mt-6 max-w-xl text-sm font-light leading-loose text-neutral-300">
          {done.payment === "paid"
            ? "Your participation fee of Rs 999 is confirmed and your application is complete. The house of D'La Valentina will reach out with the grooming schedule and venue details."
            : "Welcome to the house of D'La Valentina. Our team will review your application and reach out to you on your mobile or WhatsApp with the next steps, participation fee details and grooming schedule."}
        </p>
        {done.payment === "paid" && (
          <p data-testid="registration-paid-badge" className="mt-4 border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-6 py-3 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Rs 999 Paid — Application Confirmed
          </p>
        )}
        <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-neutral-500">
          Reference: <span className="text-[#D4AF37]">{done.id.slice(0, 8).toUpperCase()}</span>
        </p>
        <Link to="/" data-testid="registration-success-home" className="mt-10 border border-[#D4AF37]/60 px-10 py-4 text-xs uppercase tracking-[0.3em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-black">
          Return to the House
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="registration-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-6">
          <Link to="/" data-testid="registration-back-home" className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-neutral-300 transition-colors hover:text-[#D4AF37]">
            <ArrowLeft className="h-4 w-4" strokeWidth={1} /> Back to the House
          </Link>
          <span className="font-script text-2xl text-[#D4AF37]">D'La Valentina</span>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="mb-14 text-center">
          <img src="/brand/logo.png" alt="D'La Valentina" data-testid="registration-logo" className="mx-auto h-32 w-auto" />
          <h1 className="font-display mt-6 text-4xl font-medium md:text-5xl">Official Entry Form</h1>
          <p className="mx-auto mt-5 max-w-xl text-sm font-light leading-relaxed text-neutral-400">
            Application D'La Valentina — Model & Talent Hunt. From little stars of 4 to the elegance of 40.
            Fields marked <span className="text-[#D4AF37]">*</span> are required.
          </p>
        </div>

        <form data-testid="registration-form" onSubmit={submit} className="space-y-8">
          <Section num="01" title="Personal Details">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Family Name" name="family_name" form={form} set={set} required testid="reg-family-name" />
              <Field label="First Name" name="first_name" form={form} set={set} required testid="reg-first-name" />
              <div>
                <label className={labelCls}>Sex</label>
                <select data-testid="reg-sex" value={form.sex} onChange={set("sex")} className={`${inputCls} appearance-none`}>
                  {["Female", "Male", "Other"].map((s) => (
                    <option key={s} value={s} className="bg-black">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Pageant Category <span className="text-[#D4AF37]">*</span></label>
                <select data-testid="reg-category" value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-black">{c}</option>
                  ))}
                </select>
              </div>
              <Field label="Age" name="age" form={form} set={set} type="number" testid="reg-age" />
              <Field label="Date of Birth" name="dob" form={form} set={set} type="date" required testid="reg-dob" />
              <div className="md:col-span-2">
                <Field label="Passport Number (if available)" name="passport" form={form} set={set} testid="reg-passport" />
              </div>
            </div>
          </Section>

          <Section num="02" title="Contact Details">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="E-mail" name="email" form={form} set={set} type="email" required testid="reg-email" />
              <Field label="Mobile" name="phone" form={form} set={set} type="tel" required testid="reg-phone" />
              <Field label="Parent's Name (for contestants under 18)" name="parent_name" form={form} set={set} testid="reg-parent-name" />
              <Field label="Parent's Mobile Number" name="parents_mobile" form={form} set={set} type="tel" testid="reg-parents-mobile" />
              <Field label="Address" name="address" form={form} set={set} testid="reg-address" />
              <Field label="City" name="city" form={form} set={set} testid="reg-city" />
            </div>
          </Section>

          <Section num="03" title="Measurements & Talents">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Height (cm)" name="height" form={form} set={set} placeholder="e.g. 140" testid="reg-height" />
              <Field label="Weight (kg)" name="weight" form={form} set={set} placeholder="e.g. 35" testid="reg-weight" />
              <Field label="Hobby" name="hobby" form={form} set={set} testid="reg-hobby" />
              <Field label="Talent" name="talent" form={form} set={set} testid="reg-talent" />
              <div className="md:col-span-2">
                <Field label="Achievements (a, b, c...)" name="achievements" form={form} set={set} textarea placeholder="List your achievements, one per line" testid="reg-achievements" />
              </div>
              <div className="md:col-span-2">
                <Field label="How did you hear about us?" name="heard_from" form={form} set={set} testid="reg-heard-from" />
              </div>
            </div>
          </Section>

          <Section num="04" title="Photographs">
            <div className="grid gap-5 md:grid-cols-2">
              <PhotoSlot label="Close-up Photograph" hint="Upload Close-up" value={form.photo_closeup} onChange={setPhoto("photo_closeup")} testid="reg-photo-closeup" />
              <PhotoSlot label="Full-length Photograph" hint="Upload Full-length" value={form.photo_fulllength} onChange={setPhoto("photo_fulllength")} testid="reg-photo-fulllength" />
            </div>
            <p className="mt-4 text-[11px] font-light tracking-wide text-neutral-500">
              Clear, recent photographs in good light. JPG or PNG, up to 8MB each.
            </p>
          </Section>

          <Section num="05" title="Declaration">
            <p className="text-sm font-light leading-relaxed text-neutral-300">
              Participation fee: <span className="font-display text-xl text-[#D4AF37]">Rs 999/-</span> per contestant —
              payable securely online immediately after you submit this form (UPI, cards & net banking accepted).
            </p>
            <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm font-light text-neutral-300">
              <input data-testid="reg-agree-checkbox" type="checkbox" checked={form.agreed} onChange={set("agreed")} className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" />
              I declare that the above information is true and I agree to pay Rs 999/- towards participation fees.
              For contestants under 18, this application is submitted with the consent of the parent or guardian
              named above.
            </label>
            <button
              data-testid="reg-submit-button"
              type="submit"
              disabled={submitting}
              className="mt-8 flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-8 py-5 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#F9F1CC] disabled:opacity-50"
            >
              <Crown className="h-4 w-4" strokeWidth={1.5} />
              {submitting ? "Submitting Your Application..." : "Submit My Application"}
            </button>
          </Section>
        </form>
      </div>
    </div>
  );
}
